// Admin → Dashboard/Comp/RecordTable/MoreDropDown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Classes from './RecordTable.module.css';

/* ---------- Calendar helpers ---------- */
function pad(n) { return String(n).padStart(2, '0'); }
function toICSDate(dt) {
  const d = new Date(dt);
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}
function toGoogleDateRange(startISO, endISO) {
  return `${toICSDate(startISO)}/${toICSDate(endISO)}`;
}
function safeText(s) { return (s || '').toString().trim(); }
function buildLocation(b) {
  const loc = b?.location || {};
  const parts = [
    loc.address1 || loc.address || '',
    loc.city || '',
    loc.region || loc.state || '',
    loc.postalCode || '',
    loc.country || ''
  ].filter(Boolean);
  return parts.join(', ');
}
function defaultDurationMs(ms = 60 * 60 * 1000) { return ms; } // 1 hour
function isIOS() { return /iPad|iPhone|iPod/.test(window.navigator.userAgent); }

function buildICS({ uid, title, desc, location, start, end }) {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FuelClubs//Appointments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(start)}`,
    `DTEND:${toICSDate(end)}`,
    `SUMMARY:${title.replace(/\n/g, ' ')}`,
    `DESCRIPTION:${desc.replace(/\n/g, ' ')}`,
    `LOCATION:${location.replace(/\n/g, ' ')}`,
    'END:VEVENT',
    'END:VCALENDAR',
    ''
  ].join('\r\n');
}

function openAppleCalendar(icsText, filename = 'FuelClubs-Appointment.ics') {
  if (isIOS()) {
    const dataUrl = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(icsText);
    window.location.href = dataUrl; // opens Apple Calendar add sheet on iOS
  } else {
    const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export default function MoreDropDown({ booking, row, onView, onEdit }) {
  const [dropDown, setDropDown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropDown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ----- Calendar generators -----
  const createGoogleCalendarUrl = () => {
    const title = safeText(`Fuel Delivery${row?.client ? ` – ${row.client}` : ''}`);
    const details = safeText(`Service Type: ${row?.serviceType || 'Fuel Delivery'}`);
    const location = safeText(buildLocation(booking));
    const start = booking?.appointment?.date ? new Date(booking.appointment.date) : new Date();
    const end = new Date(start.getTime() + defaultDurationMs());
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      details,
      location,
      dates: toGoogleDateRange(start, end),
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const openGoogle = () => {
    window.open(createGoogleCalendarUrl(), '_blank', 'noopener,noreferrer');
    setDropDown(false);
  };

  const addToApple = () => {
    const uid = booking?._id || `${Date.now()}@fuelclubs`;
    const title = safeText(`Fuel Delivery${row?.client ? ` – ${row.client}` : ''}`);
    const desc = safeText(`Service Type: ${row?.serviceType || 'Fuel Delivery'}`);
    const location = safeText(buildLocation(booking));
    const start = booking?.appointment?.date ? new Date(booking.appointment.date) : new Date();
    const end = new Date(start.getTime() + defaultDurationMs());
    const ics = buildICS({ uid, title, desc, location, start, end });
    openAppleCalendar(ics, `FuelClubs-${(row?.client || 'Appointment').replace(/\s+/g, '-')}.ics`);
    setDropDown(false);
  };

  return (
    <>
      <div className={Classes.dropDown} ref={dropdownRef}>
        <div className="flexAlign10 pointer mr10" onClick={() => setDropDown(!dropDown)}>
          <div className={Classes.dropdownIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" height='15px' viewBox="0 0 128 512">
              <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
            </svg>
          </div>
        </div>

        <AnimatePresence>
          {dropDown && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.15 }}
              className={Classes.dropDownBody}
              style={{ top: '13px', right: '30px' }}
            >
              <div className={Classes.dropDownBodyInner}>
                <div
                  className={Classes.dropDownElem}
                  style={{ padding: '5px 10px' }}
                  onClick={() => { setDropDown(false); onView?.(booking); }}
                >
                  <h5>View appointment</h5>
                </div>

                <div className={Classes.dropDownLine}></div>

                <div
                  className={Classes.dropDownElem}
                  style={{ padding: '5px 10px' }}
                  onClick={() => { setDropDown(false); onEdit?.(booking); }}
                >
                  <h5>Edit appointment</h5>
                </div>

                <div className={Classes.dropDownLine}></div>

                {/* <div
                  className={Classes.dropDownElem}
                  style={{ padding: '5px 10px' }}
                  onClick={openGoogle}
                >
                  <h5>Add to Google Calendar</h5>
                </div>

                <div
                  className={Classes.dropDownElem}
                  style={{ padding: '5px 10px' }}
                  onClick={addToApple}
                >
                  <h5>Add to Apple Calendar</h5>
                </div> */}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <input type="hidden" aria-label="Without label" readOnly />
      </div>
    </>
  );
}
